import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, ExternalLink, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const WINDOWS = [
  { key: "24h", label: "Last 24 hours" },
  { key: "7d",  label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

const STATUS_STYLE = {
  sent:    { chip: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Sent" },
  failed:  { chip: "bg-red-100 text-red-800 border-red-200",             label: "Failed" },
  skipped: { chip: "bg-slate-100 text-slate-700 border-slate-200",       label: "Skipped" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU"); } catch { return "—"; }
}

export default function AdminPartnerNotifyHealth() {
  const [window, setWindow] = useState("7d");
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/partner-notify-health/summary?window=${window}`),
      api.get(`/admin/partner-notify-health/events?window=${window}`),
    ]).then(([s, e]) => {
      setSummary(s.data);
      setEvents(e.data?.rows || []);
    }).finally(() => setLoading(false));
  };

  useEffect(load, [window]);   // eslint-disable-line react-hooks/exhaustive-deps

  const retry = async (row) => {
    setRetrying(row.booking_reference);
    try {
      const r = await api.post(
        `/admin/partner-notify-health/retry/${encodeURIComponent(row.booking_reference)}`,
      );
      const s = r.data?.retry?.status;
      if (s === "sent") toast.success(`Notification sent to ${r.data.retry.to}`);
      else toast.warning(`Retry outcome: ${s}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Retry failed";
      toast.error(detail);
    } finally { setRetrying(null); }
  };

  const canRetry = (row) => (
    row.status === "failed" &&
    row.booking_reference &&
    !row.provider_id
  );

  const totalNeedsAttention = summary?.failed || 0;

  return (
    <PortalShell title="Partner Notification Health" subtitle="Admin" wide>
      <div className="space-y-6" data-testid="admin-partner-notify-health-page">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-cyan-700">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        {/* Header summary */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">
              customer_report_published — {WINDOWS.find(w => w.key === window)?.label}
            </div>
            <div className="mt-2 flex items-baseline gap-4 flex-wrap">
              <Counter label="Sent" value={summary?.sent} color="text-emerald-700" testid="notify-health-drill-sent" />
              <Counter label={`Opened${summary?.sent ? ` (${Math.round((summary.open_rate || 0) * 100)}%)` : ""}`} value={summary?.opened} color="text-slate-700" testid="notify-health-drill-opened" />
              <Counter label={`Clicked${summary?.sent ? ` (${Math.round((summary.click_rate || 0) * 100)}%)` : ""}`} value={summary?.clicked} color="text-emerald-700" testid="notify-health-drill-clicked" />
              <Counter label="Failed" value={summary?.failed} color={summary?.failed ? "text-red-700" : "text-slate-400"} testid="notify-health-drill-failed" />
              <Counter label="Retryable" value={summary?.retryable} color={summary?.retryable ? "text-amber-700" : "text-slate-400"} testid="notify-health-drill-retryable" />
              <Counter label="Skipped" value={summary?.skipped} color="text-slate-500" testid="notify-health-drill-skipped" />
              <Counter label="Total attempts" value={summary?.total_attempts} color="text-slate-700" testid="notify-health-drill-total" />
              {totalNeedsAttention > 0 && (
                <div className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-[11px] font-semibold px-2 py-1">
                  <AlertTriangle className="w-3 h-3" /> Needs attention: {totalNeedsAttention}
                </div>
              )}
              {summary && summary.needs_attention === false && (
                <div className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-[11px] font-semibold px-2 py-1">
                  <CheckCircle2 className="w-3 h-3" /> All healthy
                </div>
              )}
            </div>
          </div>
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger className="h-10 w-44 rounded-xl" data-testid="notify-health-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
          </div>
        )}

        {!loading && events.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500 text-sm">
            No partner-report notifications in this window.
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-3 px-4">Booking</th>
                    <th className="text-left py-3 px-4">Customer</th>
                    <th className="text-left py-3 px-4">Partner</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Last attempt</th>
                    <th className="text-left py-3 px-4">Sent</th>
                    <th className="text-left py-3 px-4">Opened</th>
                    <th className="text-left py-3 px-4">Clicked</th>
                    <th className="text-left py-3 px-4">Reason / Error</th>
                    <th className="py-3 pr-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {events.map((r, i) => {
                    const style = STATUS_STYLE[r.status] || STATUS_STYLE.skipped;
                    return (
                    <tr key={`${r.report_id}_${i}`} className="hover:bg-slate-50/60" data-testid={`notify-health-row-${r.booking_reference || r.report_id}`}>
                      <td className="py-3 px-4 font-mono text-xs text-slate-700">
                        {r.admin_report_url ? (
                          <Link to={r.admin_report_url} className="hover:text-cyan-700 inline-flex items-center gap-1">
                            {r.booking_reference || "—"} <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : (r.booking_reference || "—")}
                      </td>
                      <td className="py-3 px-4 text-slate-700">{r.customer_name || "—"}</td>
                      <td className="py-3 px-4 text-slate-700">
                        {r.admin_partner_url ? (
                          <Link to={r.admin_partner_url} className="hover:text-cyan-700 inline-flex items-center gap-1">
                            {r.partner_name || r.partner_id || "—"} <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : (r.partner_name || r.partner_id || "—")}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex text-[11px] font-medium rounded-full border px-2 py-0.5 ${style.chip}`}>
                          {style.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{fmt(r.last_attempt_at)}</td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{fmt(r.sent_at)}</td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{fmt(r.opened_at)}</td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{fmt(r.clicked_at)}</td>
                      <td className="py-3 px-4 text-xs text-slate-600 max-w-[280px] truncate" title={r.error || r.skip_reason || ""}>
                        {r.error || r.skip_reason || "—"}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {canRetry(r) && (
                          <button
                            onClick={() => retry(r)}
                            disabled={retrying === r.booking_reference}
                            data-testid={`notify-health-retry-${r.booking_reference}`}
                            className="inline-flex items-center gap-1 rounded-lg bg-amber-600 text-white text-xs font-semibold px-2.5 py-1.5 hover:bg-amber-700 disabled:opacity-50"
                          >
                            {retrying === r.booking_reference
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <RefreshCw className="w-3 h-3" />}
                            Retry
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function Counter({ label, value, color, testid }) {
  return (
    <div>
      <div className={`text-2xl font-bold tabular-nums ${color}`} data-testid={testid}>{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}
